/**
 * @file Builds the individual profile schema: the liability, marital,
 * citizenship, residency and SSN inputs.
 */
import { TYPE_BOOLEAN, TYPE_SSN, TYPE_NONE } from "@components/Form";

import InvestorSsnNumber from "../InvestorSsnNumber";
import computeLiabilityInputs from "./computeLiabilityInputs";
import computeResidencyInputs from "./computeResidencyInputs";
import computeIsUsCitizenInput from "./computeIsUsCitizenInput";
import type {
  Input,
  Organization,
  UpdateSchema,
  ResidencyLabels,
  GetInvestorSsnNumber,
  FormProfileIndividual,
} from "../types";

const LABEL_IS_US_PERSON = "Under U.S. law, are you considered a U.S. Person — a U.S. citizen or U.S. resident — expected to file a resident federal income tax return (Form 1040), not a nonresident return (Form 1040-NR)?";
const TOOLTIP_IS_US_PERSON = "Answer Yes if you hold a green card (wherever you live) or meet the IRS substantial presence test — that is, when you file, you file as a resident on Form 1040. If you are unsure which return applies to you, consult your tax advisor.";
const LABEL_HAS_TIN = "Do you have a US TIN";
const LABEL_HAS_SSN = "Do you have an SSN?";
const LABEL_TIN = "Your US TIN";
const LABEL_SSN = "Your Social Security Number";
const LABEL_TITLE = "Title of membership interests";
const LABEL_SINGLE = "Individually, as a single person";
const LABEL_MARRIED = "A married person, as my sole and separate property";
const LABEL_IS_US_CITIZEN = "Are you a US citizen?";

/**
 * Builds the individual profile inputs, hiding the SSN input until the
 * investor confirms they have one.
 */
const getProfileIndividualSchema = (
  organization: Organization,
  updateSchema: UpdateSchema,
  getInvestorSsnNumber: GetInvestorSsnNumber,
  profile: FormProfileIndividual = {},
): Input[] => {
  const { email: organizationEmail } = organization;

  let ssnType = TYPE_SSN;
  const { hasSsn, isUsPerson, isUsCitizen } = profile;
  const isNotUsPerson = !isUsCitizen && isUsPerson === false;

  if (!hasSsn) {
    ssnType = TYPE_NONE;
  }

  const labels: ResidencyLabels = {
    isUsPerson: LABEL_IS_US_PERSON,
    isUsPersonTooltip: TOOLTIP_IS_US_PERSON,
    hasSsn: isNotUsPerson ? LABEL_HAS_TIN : LABEL_HAS_SSN,
  };
  const residencyInputs = computeResidencyInputs(profile, updateSchema, labels, organizationEmail);
  const liabilityInputs = computeLiabilityInputs(updateSchema, organization);

  const inputs: Input[] = [
    ...liabilityInputs,
    {
      name: "isMarriedAndSeparateProperty",
      type: TYPE_BOOLEAN,
      label: LABEL_TITLE,
      required: true,
      inputProps: {
        vertical: "true",
        options: [
          {
            label: LABEL_SINGLE,
            value: "false",
          },
          {
            label: LABEL_MARRIED,
            value: "true",
          },
        ],
      },
    },
    computeIsUsCitizenInput(LABEL_IS_US_CITIZEN, updateSchema),
    ...residencyInputs,
    {
      name: "ssnNumber",
      component: InvestorSsnNumber,
      type: ssnType,
      componentProps: {
        required: true,
        itemProps: {
          initialValue: getInvestorSsnNumber(),
          label: isNotUsPerson ? LABEL_TIN : LABEL_SSN,
        },
      },
    },
  ];

  return inputs;
};

export default getProfileIndividualSchema;
